(() => {
  const onReady = (callback) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
      return;
    }

    callback();
  };

  const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const bindHomepageHeader = () => {
    const header = document.querySelector("#header");
    const slider = document.querySelector("#slider");

    if (header?.dataset.silmeaHeaderBound === "true") {
      return;
    }

    if (header) {
      header.dataset.silmeaHeaderBound = "true";
    }

    const menuTrigger = document.querySelector(".headerNavLink.menu");
    const dropdownNav = document.querySelector("#dropdownNav");
    const dropdownItem = dropdownNav?.querySelector(".dropdownNavItem");
    const dropdownExit = document.querySelector("#dropdownExit");
    const tabs = Array.from(document.querySelectorAll(".dropdownNavItemControlsItem"));
    const panels = Array.from(document.querySelectorAll(".dropdownNavItemLinksGroup"));
    const openers = Array.from(document.querySelectorAll(".openNav"));
    const closers = Array.from(document.querySelectorAll(".closeNav"));
    const mobileToggles = Array.from(document.querySelectorAll(".sliderNavDropdownToggle"));

    let menuCloseTimer = null;

    const activateTab = (tab, focusTab = false) => {
      if (!tab) {
        return;
      }

      tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle("active", isActive);
        item.setAttribute("aria-selected", String(isActive));
        item.setAttribute("aria-expanded", String(isActive));
        item.setAttribute("tabindex", isActive ? "0" : "-1");
      });

      panels.forEach((panel) => {
        panel.classList.toggle("active", panel.id === tab.dataset.target);
      });

      if (focusTab) {
        tab.focus();
      }
    };

    const openMenu = () => {
      if (!menuTrigger || !dropdownItem || !dropdownExit || !dropdownNav) {
        return;
      }

      window.clearTimeout(menuCloseTimer);
      menuTrigger.classList.add("active");
      menuTrigger.setAttribute("aria-expanded", "true");
      dropdownItem.classList.add("active");
      dropdownExit.classList.add("active");
      dropdownNav.removeAttribute("inert");
    };

    const closeMenu = () => {
      if (!menuTrigger || !dropdownItem || !dropdownExit || !dropdownNav) {
        return;
      }

      menuTrigger.classList.remove("active");
      menuTrigger.setAttribute("aria-expanded", "false");
      dropdownItem.classList.remove("active");
      dropdownExit.classList.remove("active");
      dropdownNav.setAttribute("inert", "");
    };

    if (menuTrigger && dropdownItem && dropdownExit && dropdownNav && tabs.length && panels.length) {
      activateTab(tabs[0]);
      menuTrigger.addEventListener("mouseenter", openMenu);
      menuTrigger.addEventListener("click", () => {
        if (dropdownItem.classList.contains("active")) {
          closeMenu();
        } else {
          openMenu();
        }
      });
      dropdownExit.addEventListener("mouseenter", () => {
        menuCloseTimer = window.setTimeout(closeMenu, 150);
      });
      dropdownExit.addEventListener("click", closeMenu);
      dropdownNav.addEventListener("mouseenter", () => window.clearTimeout(menuCloseTimer));
      tabs.forEach((tab, index) => {
        tab.addEventListener("mouseenter", () => activateTab(tab));
        tab.addEventListener("focus", () => activateTab(tab));
        tab.addEventListener("click", () => activateTab(tab));
        tab.addEventListener("keydown", (event) => {
          let nextIndex = null;

          if (event.key === "Tab" && event.shiftKey) {
            event.preventDefault();
            menuTrigger.focus();
            return;
          }

          if (event.key === "ArrowDown" || event.key === "ArrowRight") {
            nextIndex = (index + 1) % tabs.length;
          } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
            nextIndex = (index - 1 + tabs.length) % tabs.length;
          } else if (event.key === "Home") {
            nextIndex = 0;
          } else if (event.key === "End") {
            nextIndex = tabs.length - 1;
          }

          if (nextIndex !== null) {
            event.preventDefault();
            activateTab(tabs[nextIndex], true);
          }
        });
      });

      menuTrigger.addEventListener("keydown", (event) => {
        if (event.key === "Tab" && !event.shiftKey && dropdownItem.classList.contains("active")) {
          event.preventDefault();
          const activeTab = tabs.find((tab) => tab.classList.contains("active")) || tabs[0];
          activeTab.focus();
        }
      });

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && dropdownItem.classList.contains("active")) {
          closeMenu();
          menuTrigger.focus();
        }
      });

      document.addEventListener("focusin", (event) => {
        if (!dropdownItem.classList.contains("active")) {
          return;
        }

        if (!menuTrigger.contains(event.target) && !dropdownNav.contains(event.target)) {
          closeMenu();
        }
      });
    }

    const openSlider = () => {
      if (!slider) {
        return;
      }

      if (typeof slider.showModal === "function") {
        if (!slider.open) {
          slider.showModal();
        }
      } else {
        slider.setAttribute("open", "");
      }

      document.body.classList.add("noScroll");
    };

    const closeSlider = () => {
      if (!slider) {
        return;
      }

      if (typeof slider.close === "function" && slider.open) {
        slider.close();
      } else {
        slider.removeAttribute("open");
      }

      document.body.classList.remove("noScroll");
    };

    openers.forEach((element) => {
      element.addEventListener("click", openSlider);
    });

    closers.forEach((element) => {
      element.addEventListener("click", closeSlider);
    });

    slider?.addEventListener("cancel", (event) => {
      event.preventDefault();
      closeSlider();
    });

    slider?.addEventListener("close", () => {
      document.body.classList.remove("noScroll");
    });

    slider?.addEventListener("click", (event) => {
      if (event.target === slider) {
        closeSlider();
      }
    });

    mobileToggles.forEach((toggle) => {
      toggle.addEventListener("click", () => {
        const expanded = toggle.classList.toggle("active");
        toggle.setAttribute("aria-expanded", String(expanded));
      });
    });
  };

  const bindHeroSlideshow = () => {
    document.querySelectorAll("#silmea-hero-slideshow").forEach((root) => {
      if (root.dataset.silmeaHeroBound === "true") {
        return;
      }

      const slides = Array.from(root.querySelectorAll(".heroV3Slide"));
      const prev = root.querySelector(".heroV3ControlsBtn.prev");
      const next = root.querySelector(".heroV3ControlsBtn.next");
      const bullets = Array.from(root.querySelectorAll(".heroV3ControlsPag .swiper-pagination-bullet"));

      if (!slides.length) {
        return;
      }

      root.dataset.silmeaHeroBound = "true";

      let index = Math.max(
        slides.findIndex((slide) => slide.classList.contains("swiper-slide-active")),
        0
      );
      let timer = null;

      const setButtonState = (button) => {
        if (!button) {
          return;
        }

        const shouldDisable = slides.length <= 1;
        button.disabled = shouldDisable;
        button.classList.toggle("swiper-button-disabled", shouldDisable);
        button.setAttribute("aria-disabled", String(shouldDisable));
        button.setAttribute("tabindex", shouldDisable ? "-1" : "0");
      };

      const render = () => {
  slides.forEach((slide, slideIndex) => {
    const isActive = slideIndex === index;

    slide.classList.toggle("swiper-slide-active", isActive);
    slide.classList.toggle("swiper-slide-next", slideIndex === (index + 1) % slides.length);
    slide.classList.toggle("swiper-slide-prev", slideIndex === (index - 1 + slides.length) % slides.length);

    slide.hidden = false;
    slide.style.display = "";
    slide.style.opacity = isActive ? "1" : "0";
    slide.style.visibility = isActive ? "visible" : "hidden";
    slide.style.pointerEvents = isActive ? "auto" : "none";
    slide.style.zIndex = isActive ? "2" : "1";

    slide.setAttribute("aria-hidden", String(!isActive));
  });

        bullets.forEach((bullet, bulletIndex) => {
          const isActive = bulletIndex === index;
          bullet.classList.toggle("swiper-pagination-bullet-active", isActive);
          bullet.setAttribute("aria-current", isActive ? "true" : "false");
          bullet.setAttribute("aria-label", `Go to slide ${bulletIndex + 1}`);
          bullet.setAttribute("role", "button");
          bullet.setAttribute("tabindex", "0");
        });

        setButtonState(prev);
        setButtonState(next);
      };

      const stop = () => {
        if (timer) {
          window.clearInterval(timer);
          timer = null;
        }
      };

      const start = () => {
        stop();
        if (slides.length > 1 && !prefersReducedMotion()) {
          timer = window.setInterval(() => goTo(index + 1, false), 7000);
        }
      };

      const goTo = (targetIndex, userInitiated = true) => {
        index = (targetIndex + slides.length) % slides.length;
        render();

        if (userInitiated) {
          start();
        }
      };

      prev?.addEventListener("click", () => goTo(index - 1));
      next?.addEventListener("click", () => goTo(index + 1));

      bullets.forEach((bullet, bulletIndex) => {
        bullet.addEventListener("click", () => goTo(bulletIndex));
        bullet.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") {
            return;
          }

          event.preventDefault();
          goTo(bulletIndex);
        });
      });

      root.addEventListener("mouseenter", stop);
      root.addEventListener("mouseleave", start);
      root.addEventListener("focusin", stop);
      root.addEventListener("focusout", start);

      document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
          stop();
        } else {
          start();
        }
      });

      render();
      start();
    });
  };

  const bindIngredientTabs = () => {
    document.querySelectorAll("#silmea-ingredients").forEach((root) => {
      if (root.dataset.silmeaIngredientsBound === "true") {
        return;
      }

      const tabs = Array.from(root.querySelectorAll("[data-ingredients-tab]"));
      const panels = Array.from(root.querySelectorAll("[data-ingredients-panel]"));

      if (!tabs.length || !panels.length) {
        return;
      }

      root.dataset.silmeaIngredientsBound = "true";

      const activate = (name) => {
        tabs.forEach((tab) => {
          const isActive = tab.dataset.ingredientsTab === name;
          tab.classList.toggle("is-active", isActive);
          tab.setAttribute("aria-selected", String(isActive));
          tab.setAttribute("tabindex", isActive ? "0" : "-1");
        });

        panels.forEach((panel) => {
          const isActive = panel.dataset.ingredientsPanel === name;
          panel.classList.toggle("is-active", isActive);
          panel.hidden = !isActive;
          panel.style.display = isActive ? "" : "none";
        });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener("click", () => activate(tab.dataset.ingredientsTab || ""));
        tab.addEventListener("keydown", (event) => {
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
            return;
          }

          event.preventDefault();
          const nextIndex =
            event.key === "ArrowRight"
              ? (index + 1) % tabs.length
              : (index - 1 + tabs.length) % tabs.length;

          tabs[nextIndex].focus();
          activate(tabs[nextIndex].dataset.ingredientsTab || "");
        });
      });

      const activeTab =
        tabs.find((tab) => tab.classList.contains("is-active")) ||
        tabs.find((tab) => tab.getAttribute("aria-selected") === "true") ||
        tabs[0];

      activate(activeTab.dataset.ingredientsTab || tabs[0].dataset.ingredientsTab || "");
    });
  };

  const bindPressCarousel = () => {
    document.querySelectorAll("#silmea-press-logos .logosSwiper").forEach((root) => {
      const track = root.querySelector(".logosSwiperWrapper");

      if (!track || track.dataset.silmeaPressBound === "true") {
        return;
      }

      const originalItems = Array.from(track.children);
      if (originalItems.length < 2) {
        return;
      }

      track.dataset.silmeaPressBound = "true";
      track.style.transform = "translate3d(0, 0, 0)";

      let loopWidth = 0;
      let offset = 0;
      let rafId = 0;
      let isPaused = false;

      const removeClones = () => {
        track.querySelectorAll("[data-silmea-logo-clone='true']").forEach((clone) => clone.remove());
      };

      const cloneOriginalSet = () => {
        originalItems.forEach((node) => {
          const clone = node.cloneNode(true);
          clone.dataset.silmeaLogoClone = "true";
          clone.setAttribute("aria-hidden", "true");
          clone.setAttribute("tabindex", "-1");
          clone.querySelectorAll("a, button").forEach((interactive) => {
            interactive.setAttribute("tabindex", "-1");
          });
          clone.querySelectorAll("img").forEach((img) => img.setAttribute("alt", ""));
          track.appendChild(clone);
        });
      };

      const measure = () => {
        removeClones();
        cloneOriginalSet();

        let safety = 0;
        while (track.scrollWidth < root.offsetWidth * 2.25 && safety < 8) {
          cloneOriginalSet();
          safety += 1;
        }

        const firstClone = track.querySelector("[data-silmea-logo-clone='true']");
        loopWidth = firstClone
          ? firstClone.getBoundingClientRect().left - originalItems[0].getBoundingClientRect().left
          : track.scrollWidth / 2;
        offset = Math.min(offset, Math.max(loopWidth - 1, 0));
      };

      const step = () => {
        if (!isPaused && loopWidth > 0) {
          offset += window.innerWidth <= 800 ? 0.45 : 0.65;
          if (offset >= loopWidth) {
            offset = 0;
          }

          track.style.transform = `translate3d(${-offset}px, 0, 0)`;
        }

        rafId = window.requestAnimationFrame(step);
      };

      const start = () => {
        if (!rafId && !prefersReducedMotion()) {
          rafId = window.requestAnimationFrame(step);
        }
      };

      const stop = () => {
        if (rafId) {
          window.cancelAnimationFrame(rafId);
          rafId = 0;
        }
      };

      root.addEventListener("mouseenter", () => {
        isPaused = true;
      });
      root.addEventListener("mouseleave", () => {
        isPaused = false;
      });
      root.addEventListener("focusin", () => {
        isPaused = true;
      });
      root.addEventListener("focusout", () => {
        isPaused = false;
      });

      window.addEventListener("resize", measure);
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
          stop();
        } else {
          start();
        }
      });

      originalItems
        .flatMap((item) => Array.from(item.querySelectorAll("img")))
        .forEach((img) => {
          if (!img.complete) {
            img.addEventListener("load", measure, { once: true });
          }
        });

      measure();
      start();
    });
  };

  const bindStickyCta = () => {
    const sticky = document.querySelector("#silmea-sticky-cta");
    if (!sticky || sticky.dataset.silmeaStickyBound === "true") {
      return;
    }

    sticky.dataset.silmeaStickyBound = "true";

    const updateStickyState = () => {
      const trigger = window.innerWidth <= 800 ? 400 : 1500;
      sticky.classList.toggle("active", window.scrollY > trigger);
    };

    updateStickyState();
    window.addEventListener("scroll", updateStickyState, { passive: true });
    window.addEventListener("resize", updateStickyState);
  };

  const bindSmoothScroll = () => {
    if (document.documentElement.dataset.silmeaSmoothScrollBound === "true") {
      return;
    }

    document.documentElement.dataset.silmeaSmoothScrollBound = "true";

    document.addEventListener("click", (event) => {
      const link = event.target.closest?.('a[href^="#"]');
      if (!link) {
        return;
      }

      const targetId = link.getAttribute("href")?.slice(1);
      if (!targetId) {
        return;
      }

      const target = document.getElementById(targetId);
      if (!target) {
        return;
      }

      event.preventDefault();
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const initHomepage = () => {
    bindHomepageHeader();
    bindHeroSlideshow();
    bindIngredientTabs();
    bindPressCarousel();
    bindStickyCta();
    bindSmoothScroll();
  };

  onReady(initHomepage);
  document.addEventListener("shopify:section:load", initHomepage);
})();
